import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import QRCode from 'qrcode';
import { useTranslation } from 'react-i18next';
import {
  DollarSign, TrendingUp, Clock, Tag, Plus, Edit2, Trash2,
  Download, ChevronUp, ChevronDown, Paperclip, X, Receipt,
  Filter, Search, Upload, CheckCircle, AlertCircle, FileText,
} from 'lucide-react';
import { expensesAPI, propertiesAPI, fileViewUrl } from '../services/api';
import { fmt as format } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import Modal from '../components/common/Modal';
import { toast } from '../components/common/Toast';
import { errMsg } from '../utils/helpers';

// ── Constants ──────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { key: 'materials',    color: '#3b82f6' },
  { key: 'contractor',  color: '#8b5cf6' },
  { key: 'labor',       color: '#f59e0b' },
  { key: 'equipment',   color: '#10b981' },
  { key: 'permits',     color: '#ef4444' },
  { key: 'utilities',   color: '#06b6d4' },
  { key: 'insurance',   color: '#f97316' },
  { key: 'professional',color: '#ec4899' },
  { key: 'repairs',     color: '#84cc16' },
  { key: 'marketing',   color: '#a855f7' },
  { key: 'other',       color: '#6b7280' },
];

const CAT_COLOR = Object.fromEntries(CATEGORIES.map(c => [c.key, c.color]));

const METHODS = ['cash', 'check', 'credit_card', 'bank_transfer', 'ach'];
const STATUSES = ['pending', 'approved', 'paid'];

const STATUS_STYLE = {
  pending:  { bg: 'rgba(245,158,11,0.12)',  color: '#d97706',  label: 'Pending'  },
  approved: { bg: 'rgba(37,99,235,0.12)',   color: '#2563eb',  label: 'Approved' },
  paid:     { bg: 'rgba(5,150,105,0.12)',   color: '#059669',  label: 'Paid'     },
};

const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

const BLANK = {
  date: new Date().toISOString().slice(0, 10),
  category: 'materials',
  description: '',
  vendor: '',
  amount: '',
  propertyId: '',
  workOrderId: '',
  paymentMethod: 'cash',
  notes: '',
  status: 'pending',
};

// ── Monthly bar chart ─────────────────────────────────────────────────────────
function MonthlyChart({ monthly }) {
  const [hovered, setHovered] = useState(null);

  // Abbreviated Y-axis labels
  const fmtShort = (n) => {
    if (!n) return '$0';
    if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
    return `$${Math.round(n)}`;
  };

  if (!monthly?.length) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180, color: 'var(--ink-30)', fontSize: 13 }}>
      No data for this period
    </div>
  );

  const max  = Math.max(...monthly.map(m => m.total), 1);
  const now  = new Date();
  const n    = monthly.length;

  // Layout constants
  const PAD_L  = 54, PAD_R = 16, PAD_T = 28, PAD_B = 42;
  const CHART_H = 148;
  const GAP     = 8;
  // Adaptive bar width: wider when few bars, narrower when many
  const barW   = Math.min(52, Math.max(20, Math.floor(560 / n) - GAP));
  const innerW = n * (barW + GAP) - GAP;
  const svgW   = PAD_L + innerW + PAD_R;
  const svgH   = PAD_T + CHART_H + PAD_B;

  const bX = (i) => PAD_L + i * (barW + GAP);
  const bH = (v) => Math.max(4, (v / max) * CHART_H);
  const bY = (v) => PAD_T + CHART_H - bH(v);

  const GRID_PCTS = [0.25, 0.5, 0.75, 1];

  return (
    <div style={{ overflowX: 'auto', overflowY: 'visible' }}>
      <svg
        width={Math.max(svgW, 280)}
        height={svgH}
        style={{ display: 'block', overflow: 'visible' }}
      >
        {/* Baseline */}
        <line
          x1={PAD_L} y1={PAD_T + CHART_H}
          x2={PAD_L + innerW} y2={PAD_T + CHART_H}
          stroke="var(--ink-20)" strokeWidth={1.5}
        />

        {/* Y-axis gridlines + labels */}
        {GRID_PCTS.map((pct) => {
          const gy  = PAD_T + CHART_H - pct * CHART_H;
          const val = max * pct;
          return (
            <g key={pct}>
              <line
                x1={PAD_L} y1={gy} x2={PAD_L + innerW} y2={gy}
                stroke="var(--ink-10)" strokeWidth={0.75} strokeDasharray="4 3"
              />
              <text
                x={PAD_L - 7} y={gy + 4}
                textAnchor="end" fontSize={10} fill="var(--ink-30)"
              >
                {fmtShort(val)}
              </text>
            </g>
          );
        })}

        {/* Bars */}
        {monthly.map((m, i) => {
          const x   = bX(i);
          const h   = bH(m.total);
          const y   = bY(m.total);
          const cx  = x + barW / 2;
          const isCurrentMonth = m._id.year === now.getFullYear() && m._id.month === now.getMonth() + 1;
          const isHov = hovered === i;
          const fill  = isCurrentMonth ? '#6366f1' : isHov ? '#818cf8' : 'rgba(99,102,241,0.28)';
          const monthLbl = new Date(m._id.year, m._id.month - 1).toLocaleString('default', { month: 'short' });
          const showYear = n <= 7 || m._id.month === 1;
          const radius = Math.min(5, barW / 5);

          return (
            <g
              key={i}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              style={{ cursor: 'default' }}
            >
              {/* Hover tooltip */}
              {isHov && (
                <g>
                  <rect
                    x={cx - 38} y={y - 32} width={76} height={22} rx={5}
                    fill="#1a1a2e" opacity={0.9}
                  />
                  <polygon
                    points={`${cx - 5},${y - 10} ${cx + 5},${y - 10} ${cx},${y - 4}`}
                    fill="#1a1a2e" opacity={0.9}
                  />
                  <text
                    x={cx} y={y - 17}
                    textAnchor="middle" fontSize={11} fontWeight={700}
                    fill="white"
                  >
                    {fmt(m.total)}
                  </text>
                </g>
              )}

              {/* Bar */}
              <rect
                x={x} y={y} width={barW} height={h}
                rx={radius} fill={fill}
                style={{ transition: 'fill 0.15s ease' }}
              />

              {/* Month label */}
              <text
                x={cx} y={PAD_T + CHART_H + 16}
                textAnchor="middle" fontSize={10}
                fill={isHov || isCurrentMonth ? '#6366f1' : 'var(--ink-40)'}
                fontWeight={isHov || isCurrentMonth ? 600 : 400}
              >
                {monthLbl}
              </text>

              {/* Year — only for Jan or when few bars */}
              {showYear && (
                <text
                  x={cx} y={PAD_T + CHART_H + 29}
                  textAnchor="middle" fontSize={9} fill="var(--ink-25)"
                >
                  {m._id.year}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Category donut-style bars ──────────────────────────────────────────────────
function CategoryBreakdown({ byCategory }) {
  if (!byCategory?.length) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 140, color: 'var(--ink-30)', fontSize: 13 }}>
      No data yet
    </div>
  );
  const grandTotal = byCategory.reduce((s, c) => s + c.total, 0) || 1;
  const top = byCategory.slice(0, 7);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {top.map(c => {
        const pct = ((c.total / grandTotal) * 100).toFixed(1);
        const color = CAT_COLOR[c._id] || '#6b7280';
        return (
          <div key={c._id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
              <span style={{ color: 'var(--ink-70)', textTransform: 'capitalize', fontWeight: 500 }}>{c._id}</span>
              <span style={{ color: 'var(--ink-50)' }}>{fmt(c.total)} &nbsp;<span style={{ color }}>{pct}%</span></span>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: 'var(--ink-10)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.6s ease' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── KPI card ──────────────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="card" style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={20} color={color} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, color: 'var(--ink-40)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink-90)', lineHeight: 1.1 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--ink-40)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

// ── Sort header ───────────────────────────────────────────────────────────────
function SortTh({ col, label, sortCol, sortDir, onSort }) {
  const active = sortCol === col;
  return (
    <th onClick={() => onSort(col)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        {active
          ? (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)
          : <ChevronDown size={12} style={{ opacity: 0.25 }} />}
      </span>
    </th>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Expenses() {
  const { t }    = useTranslation();
  const { user } = useAuth();
  const { can }  = usePermissions();

  const canCreate = can('expenses', 'create');
  const canEdit   = can('expenses', 'edit');
  const canDelete = can('expenses', 'delete');

  // Data
  const [expenses,   setExpenses]   = useState([]);
  const [summary,    setSummary]    = useState({});
  const [properties, setProperties] = useState([]);
  const [loading,    setLoading]    = useState(true);

  // Filters
  const today      = new Date().toISOString().slice(0, 10);
  const firstOfYear = `${new Date().getFullYear()}-01-01`;
  const [filters, setFilters] = useState({ from: firstOfYear, to: today, category: '', propertyId: '', status: '', search: '' });

  // Sort
  const [sortCol, setSortCol] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  // Create/Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [form,      setForm]      = useState(BLANK);
  const [editId,    setEditId]    = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [file,      setFile]      = useState(null);
  const fileRef = useRef();

  // Import modal
  // The expense whose printable receipt is open, and whether one is being issued.
  const [receipt,      setReceipt]      = useState(null);
  const [issuing,      setIssuing]      = useState(null);

  const [importOpen,    setImportOpen]    = useState(false);
  const [importRows,    setImportRows]    = useState([]);   // { data, valid, errors }
  const [importing,     setImporting]     = useState(false);
  const importFileRef = useRef();

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.from)       params.dateFrom    = filters.from;
      if (filters.to)         params.dateTo      = filters.to;
      if (filters.category)   params.category    = filters.category;
      if (filters.propertyId) params.propertyId  = filters.propertyId;
      if (filters.status)     params.status      = filters.status;
      if (filters.search)     params.search      = filters.search;

      const [expRes, sumRes] = await Promise.all([
        expensesAPI.getAll(params),
        expensesAPI.getSummary(params),
      ]);
      setExpenses(expRes.data.data || []);
      setSummary(sumRes.data.data  || {});
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    propertiesAPI.getAll().then(r => setProperties(r.data.data || [])).catch(() => {});
  }, []);

  // ── Sort ──────────────────────────────────────────────────────────────────
  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const sorted = [...expenses].sort((a, b) => {
    let av = a[sortCol], bv = b[sortCol];
    if (sortCol === 'propertyId') { av = a.propertyId?.name || ''; bv = b.propertyId?.name || ''; }
    if (sortCol === 'date') { av = new Date(av); bv = new Date(bv); }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1  : -1;
    return 0;
  });

  // ── Form helpers ──────────────────────────────────────────────────────────
  const openCreate = () => {
    setForm(BLANK);
    setEditId(null);
    setFile(null);
    setModalOpen(true);
  };

  const openEdit = (exp) => {
    setForm({
      date:          exp.date?.slice(0, 10) || BLANK.date,
      category:      exp.category || 'other',
      description:   exp.description || '',
      vendor:        exp.vendor || '',
      amount:        exp.amount?.toString() || '',
      propertyId:    exp.propertyId?._id || exp.propertyId || '',
      workOrderId:   exp.workOrderId?._id || exp.workOrderId || '',
      paymentMethod: exp.paymentMethod || 'cash',
      notes:         exp.notes || '',
      status:        exp.status || 'pending',
    });
    setEditId(exp._id);
    setFile(null);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.description.trim()) return toast.error(t('descriptionRequired') || 'Description is required');
    if (!form.amount || isNaN(parseFloat(form.amount))) return toast.error(t('amountRequired') || 'Valid amount is required');

    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '') fd.append(k, v); });
      if (file) fd.append('receipt', file);

      if (editId) await expensesAPI.update(editId, fd);
      else        await expensesAPI.create(fd);

      toast.success(t('expenseSaved') || 'Expense saved');
      setModalOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  // Issuing is idempotent server-side, so clicking twice cannot produce a
  // second number for the same expense.
  const openReceipt = async (exp) => {
    if (exp.receiptNo) return setReceipt(exp);
    setIssuing(exp._id);
    try {
      const { data } = await expensesAPI.issueReceipt(exp._id);
      setReceipt(data.data);
      setExpenses(list => list.map(e => (e._id === exp._id ? { ...e, ...data.data } : e)));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setIssuing(null); }
  };

  const handleDelete = (id, desc) => {
    toast.confirm(
      `${t('deleteExpense') || 'Delete expense'}: ${desc}?`,
      async () => {
        try {
          await expensesAPI.delete(id);
          toast.success(t('expenseDeleted') || 'Expense deleted');
          load();
        } catch (err) { toast.error(errMsg(err)); }
      },
      t('delete') || 'Delete',
    );
  };

  // ── CSV export ────────────────────────────────────────────────────────────
  const exportCsv = () => {
    const header = ['Date', 'Category', 'Description', 'Vendor', 'Property', 'Amount', 'Method', 'Status', 'Notes'];
    const rows = sorted.map(e => [
      e.date?.slice(0, 10),
      e.category,
      `"${(e.description || '').replace(/"/g, '""')}"`,
      `"${(e.vendor || '').replace(/"/g, '""')}"`,
      e.propertyId?.name || '',
      e.amount,
      e.paymentMethod,
      e.status,
      `"${(e.notes || '').replace(/"/g, '""')}"`,
    ].join(','));
    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `expenses-${filters.from}-${filters.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── CSV import helpers ────────────────────────────────────────────────────
  const TEMPLATE_HEADERS = ['date','category','description','vendor','amount','property','paymentMethod','notes','status'];

  // Maps label (any language, any case) → canonical key
  const CAT_MAP = {
    materials:'materials','materials & supplies':'materials','materiales y suministros':'materials','materiales':'materials',
    contractor:'contractor','contractor payments':'contractor','pagos a contratistas':'contractor','contratistas':'contractor',
    labor:'labor','labor costs':'labor','costos de mano de obra':'labor','mano de obra':'labor',
    equipment:'equipment','equipment & tools':'equipment','equipos y herramientas':'equipment','equipos':'equipment',
    permits:'permits','permits & fees':'permits','permisos y tasas':'permits','permisos':'permits',
    utilities:'utilities','servicios públicos':'utilities','servicios publicos':'utilities','utilidades':'utilities',
    insurance:'insurance','seguro':'insurance','seguros':'insurance',
    professional:'professional','professional services':'professional','servicios profesionales':'professional',
    repairs:'repairs','repairs & maintenance':'repairs','reparaciones y mantenimiento':'repairs','reparaciones':'repairs',
    marketing:'marketing','mercadeo':'marketing',
    other:'other','otros':'other','otro':'other',
  };
  const METHOD_MAP = {
    cash:'cash','efectivo':'cash',
    check:'check','cheque':'check',
    credit_card:'credit_card','credit card':'credit_card','tarjeta de crédito':'credit_card','tarjeta de credito':'credit_card','tarjeta':'credit_card',
    bank_transfer:'bank_transfer','bank transfer':'bank_transfer','transferencia bancaria':'bank_transfer',
    ach:'ach','ach transfer':'ach','transferencia ach':'ach',
  };
  const STATUS_MAP = {
    pending:'pending','pendiente':'pending',
    approved:'approved','aprobado':'approved','aprobada':'approved',
    paid:'paid','pagado':'paid','pagada':'paid',
  };

  // Strip currency symbols, spaces, thousands commas → plain number string
  const sanitizeAmount = (s) => String(s || '').replace(/[$\s]/g,'').replace(/,(?=\d{3})/g,'').replace(/,/g,'');

  const downloadTemplate = () => {
    const d = new Date().toISOString().slice(0, 10);
    const row = (date, cat, desc, vendor, amount, property, method, notes, status) =>
      [date, cat, `"${desc}"`, vendor, amount, property, method, `"${notes}"`, status].join(',');

    const examples = [
      row(d, 'materials',    'Lumber and plywood - 3rd floor',      'Home Depot',      '1250.00',     '',               'cash',          'Phase 1',       'pending'),
      row(d, 'contractor',   'Foundation work payment',             'Luis Ramirez',    '10000.00',    'Mercury Palmira', 'ach',           '4th floor',     'paid'),
      row(d, 'labor',        'Daily wages - construction crew',     'John Smith',      '500.00',      '',               'bank_transfer', '',              'approved'),
      row(d, 'equipment',    'Excavator rental - 5 days',           'CAT Rental',      '3500.00',     '',               'credit_card',   '',              'pending'),
      row(d, 'permits',      'Building permit fee',                 'City Hall',       '800.00',      '',               'check',         '',              'paid'),
      row(d, 'utilities',    'Electricity - construction site',     'Power Company',   '450.00',      '',               'cash',          'June bill',     'paid'),
      row(d, 'insurance',    'General liability insurance premium', 'Allianz',         '2000.00',     '',               'bank_transfer', '',              'paid'),
      row(d, 'professional', 'Architect design fees',               'Design Studio',   '5000.00',     '',               'ach',           'Phase 2 plans', 'paid'),
      row(d, 'repairs',      'Fix roof leak - unit 4B',             "Bob's Repairs",   '750.00',      '',               'cash',          '',              'pending'),
      row(d, 'marketing',    'Property listing photos',             'Photo Studio',    '300.00',      '',               'credit_card',   '',              'paid'),
      row(d, 'other',        'Miscellaneous office supplies',       '',                '150.00',      '',               'cash',          '',              'pending'),
    ];

    // Reference block — starts with !! so the parser flags them as errors and
    // users can simply delete them before importing
    const ref = [
      ',,,,,,,,',
      '"!! DELETE THESE REFERENCE ROWS BEFORE IMPORTING !!",,,,,,,, ',
      `"VALID CATEGORIES","materials | contractor | labor | equipment | permits | utilities | insurance | professional | repairs | marketing | other",,,,,,, `,
      `"VALID PAYMENT METHODS","cash | check | credit_card | bank_transfer | ach",,,,,,, `,
      `"VALID STATUS","pending | approved | paid",,,,,,, `,
      `"DATE FORMAT","YYYY-MM-DD  (example: ${d})",,,,,,, `,
      `"AMOUNT FORMAT","Plain number — no $ or commas needed  (example: 10000.00)",,,,,,, `,
      `"PROPERTY","Must match exactly a property name in the system or leave blank",,,,,,, `,
    ];

    const csv = [TEMPLATE_HEADERS.join(','), ...examples, ...ref].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'expenses-template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  // Minimal RFC-4180 CSV parser (handles quoted fields with embedded commas/newlines)
  const parseCsv = (text) => {
    const rows = [];
    let row = [], field = '', inQuote = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i], next = text[i + 1];
      if (inQuote) {
        if (ch === '"' && next === '"') { field += '"'; i++; }
        else if (ch === '"')            { inQuote = false; }
        else                            { field += ch; }
      } else {
        if (ch === '"')         { inQuote = true; }
        else if (ch === ',')    { row.push(field.trim()); field = ''; }
        else if (ch === '\n' || (ch === '\r' && next === '\n')) {
          if (ch === '\r') i++;
          row.push(field.trim()); rows.push(row); row = []; field = '';
        } else { field += ch; }
      }
    }
    if (field || row.length) { row.push(field.trim()); rows.push(row); }
    return rows.filter(r => r.some(c => c));
  };

  const handleImportFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const parsed = parseCsv(ev.target.result);
      if (!parsed.length) return toast.error('Empty file');

      // First row = headers (normalize: lowercase, strip spaces)
      const headers  = parsed[0].map(h => h.toLowerCase().replace(/\s+/g,''));
      const dataRows = parsed.slice(1);

      const colIdx = {};
      TEMPLATE_HEADERS.forEach(h => { colIdx[h] = headers.indexOf(h); });
      // Also try alternate header spellings
      if (colIdx.paymentMethod < 0) colIdx.paymentMethod = headers.indexOf('payment_method');

      const get = (row, key) => colIdx[key] >= 0 ? (row[colIdx[key]] || '').trim() : '';

      const validated = dataRows.map((row, i) => {
        const rawCat    = get(row,'category').toLowerCase();
        const rawMeth   = get(row,'paymentmethod') || get(row,'payment_method') || 'cash';
        const rawStatus = get(row,'status') || 'pending';
        const rawAmount = sanitizeAmount(get(row,'amount'));

        const category      = CAT_MAP[rawCat]    || rawCat;
        const paymentMethod = METHOD_MAP[rawMeth.toLowerCase()] || rawMeth.toLowerCase().replace(/\s+/g,'_');
        const status        = STATUS_MAP[rawStatus.toLowerCase()] || rawStatus.toLowerCase();
        const amount        = parseFloat(rawAmount);

        const data = {
          date:        get(row,'date'),
          category,
          description: get(row,'description'),
          vendor:      get(row,'vendor'),
          amount:      rawAmount,
          property:    get(row,'property'),
          paymentMethod,
          notes:       get(row,'notes'),
          status,
        };

        const VALID_CATS  = Object.values(CAT_MAP);
        const VALID_METHS = Object.values(METHOD_MAP);
        const VALID_STATS = Object.values(STATUS_MAP);

        const errs = [];
        if (!data.description)                        errs.push('description required');
        if (isNaN(amount) || amount < 0)              errs.push(`invalid amount "${get(row,'amount')}"`);
        if (!VALID_CATS.includes(category))           errs.push(`unknown category "${get(row,'category')}"`);
        if (!VALID_METHS.includes(paymentMethod))     errs.push(`unknown method "${get(row,'paymentmethod') || get(row,'payment_method')}"`);
        if (!VALID_STATS.includes(status))            errs.push(`unknown status "${get(row,'status')}"`);
        if (data.date && isNaN(new Date(data.date).getTime())) errs.push(`invalid date "${data.date}"`);

        return { row: i + 2, data, valid: errs.length === 0, errors: errs };
      });

      setImportRows(validated);
      setImportOpen(true);
    };
    reader.readAsText(f);
    e.target.value = '';
  };

  const handleImport = async () => {
    const validRows = importRows.filter(r => r.valid).map(r => r.data);
    if (!validRows.length) return toast.error('No valid rows to import');
    setImporting(true);
    try {
      const res = await expensesAPI.import({ rows: validRows });
      const { imported, skipped } = res.data.data;
      toast.success(`${imported} ${t('expensesImported') || 'expenses imported'}${skipped ? `, ${skipped} skipped` : ''}`);
      setImportOpen(false);
      setImportRows([]);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setImporting(false); }
  };

  // ── Top category label ────────────────────────────────────────────────────
  const topCat = summary.byCategory?.[0];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="page">
      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>{t('financialExpenses') || 'Financial Expenses'}</h1>
          <p style={{ color: 'var(--ink-40)', fontSize: 13, margin: '2px 0 0' }}>
            {t('expensesSubtitle') || 'Track materials, contractors, and all operational costs'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={exportCsv} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download size={14} />{t('exportCsv') || 'Export CSV'}
          </button>
          <button className="btn btn-secondary" onClick={downloadTemplate} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FileText size={14} />{t('downloadTemplate') || 'Download Template'}
          </button>
          {canCreate && (
            <button className="btn btn-secondary" onClick={() => importFileRef.current?.click()} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Upload size={14} />{t('importCsv') || 'Import CSV'}
            </button>
          )}
          {canCreate && (
            <button className="btn btn-primary" onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Plus size={14} />{t('newExpense') || 'New Expense'}
            </button>
          )}
        </div>
        {/* Hidden file input for CSV import */}
        <input ref={importFileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImportFile} />
      </div>

      {/* ── KPI Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 20 }}>
        <KpiCard icon={DollarSign}  label={t('totalSpent')    || 'Total Spent'}       value={fmt(summary.grandTotal)} sub={`${t('paid') || 'Paid'}: ${fmt(summary.paidTotal)}`}  color="#6366f1" />
        <KpiCard icon={TrendingUp}  label={t('thisMonth')     || 'This Month'}        value={fmt(summary.thisMonth)}  color="#10b981" />
        <KpiCard icon={Clock}       label={t('pendingApproval')|| 'Pending Approval'} value={summary.pendingCount || 0} sub={t('expensesPending') || 'expenses pending'} color="#f59e0b" />
        <KpiCard icon={Tag}         label={t('topCategory')   || 'Top Category'}      value={topCat ? topCat._id.charAt(0).toUpperCase() + topCat._id.slice(1) : '—'} sub={topCat ? fmt(topCat.total) : ''} color="#8b5cf6" />
      </div>

      {/* ── Charts row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 14, marginBottom: 20 }}>
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-70)', marginBottom: 12 }}>
            {t('monthlySummary') || 'Monthly Summary'}
          </div>
          <MonthlyChart monthly={summary.monthly} />
        </div>
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-70)', marginBottom: 12 }}>
            {t('byCategory') || 'By Category'}
          </div>
          <CategoryBreakdown byCategory={summary.byCategory} />
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="card" style={{ padding: '12px 16px', marginBottom: 14 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <Filter size={14} color="var(--ink-40)" />
          <input type="date" className="form-input" value={filters.from}
            style={{ width: 142 }}
            onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} />
          <span style={{ color: 'var(--ink-40)', fontSize: 12 }}>–</span>
          <input type="date" className="form-input" value={filters.to}
            style={{ width: 142 }}
            onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} />

          <select className="form-input" value={filters.category} style={{ width: 160 }}
            onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}>
            <option value="">{t('allCategories') || 'All Categories'}</option>
            {CATEGORIES.map(c => (
              <option key={c.key} value={c.key}>{t(`cat_${c.key}`) || c.key.charAt(0).toUpperCase() + c.key.slice(1)}</option>
            ))}
          </select>

          <select className="form-input" value={filters.propertyId} style={{ width: 160 }}
            onChange={e => setFilters(f => ({ ...f, propertyId: e.target.value }))}>
            <option value="">{t('allProperties') || 'All Properties'}</option>
            {properties.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
          </select>

          <select className="form-input" value={filters.status} style={{ width: 130 }}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="">{t('allStatuses') || 'All Statuses'}</option>
            {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>

          <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-30)' }} />
            <input className="form-input" placeholder={t('search') || 'Search…'} value={filters.search}
              style={{ paddingLeft: 30, width: '100%' }}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
          </div>

          {(filters.category || filters.propertyId || filters.status || filters.search) && (
            <button className="btn btn-ghost btn-sm" onClick={() => setFilters(f => ({ ...f, category: '', propertyId: '', status: '', search: '' }))}>
              <X size={12} /> {t('clear') || 'Clear'}
            </button>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
        ) : sorted.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-40)' }}>
            <Receipt size={32} style={{ marginBottom: 8, opacity: 0.3 }} />
            <p>{t('noExpenses') || 'No expenses found'}</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <SortTh col="date"        label={t('date') || 'Date'}         sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <SortTh col="category"    label={t('category') || 'Category'} sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <th>{t('description') || 'Description'}</th>
                  <th>{t('vendor') || 'Vendor'}</th>
                  <SortTh col="propertyId"  label={t('property') || 'Property'} sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <SortTh col="amount"      label={t('amount') || 'Amount'}     sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <th>{t('method') || 'Method'}</th>
                  <SortTh col="status"      label={t('status') || 'Status'}     sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(exp => {
                  const st = STATUS_STYLE[exp.status] || STATUS_STYLE.pending;
                  const catColor = CAT_COLOR[exp.category] || '#6b7280';
                  return (
                    <tr key={exp._id}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{exp.date?.slice(0, 10)}</td>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
                          background: `${catColor}18`, color: catColor, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: catColor, flexShrink: 0 }} />
                          {t(`cat_${exp.category}`) || exp.category}
                        </span>
                      </td>
                      <td style={{ maxWidth: 220 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-80)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {exp.description}
                        </div>
                        {exp.notes && <div style={{ fontSize: 11, color: 'var(--ink-40)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{exp.notes}</div>}
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--ink-60)' }}>{exp.vendor || '—'}</td>
                      <td style={{ fontSize: 13, color: 'var(--ink-60)' }}>{exp.propertyId?.name || '—'}</td>
                      <td style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink-80)', whiteSpace: 'nowrap' }}>{fmt(exp.amount)}</td>
                      <td style={{ fontSize: 12, color: 'var(--ink-50)', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                        {exp.paymentMethod?.replace('_', ' ')}
                      </td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 20,
                          background: st.bg, color: st.color }}>
                          {st.label}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {exp.receiptKey && (
                            <a href={fileViewUrl(exp.receiptKey)} target="_blank" rel="noreferrer"
                              className="btn btn-ghost btn-sm btn-icon" title={t('viewReceipt') || 'View receipt'}>
                              <Paperclip size={13} />
                            </a>
                          )}
                          {exp.status === 'paid' && (
                            <button className="btn btn-ghost btn-sm btn-icon" disabled={issuing === exp._id}
                              onClick={() => openReceipt(exp)}
                              title={exp.receiptNo ? t('printReceipt') : t('issueReceipt')}>
                              <Receipt size={13} />
                            </button>
                          )}
                          {canEdit && (
                            <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEdit(exp)} title={t('edit') || 'Edit'}>
                              <Edit2 size={13} />
                            </button>
                          )}
                          {canDelete && (
                            <button className="btn btn-ghost btn-sm btn-icon" style={{ color: 'var(--rose)' }}
                              onClick={() => handleDelete(exp._id, exp.description)} title={t('delete') || 'Delete'}>
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--surface-2)' }}>
                  <td colSpan={5} style={{ fontWeight: 700, fontSize: 13, padding: '10px 12px', color: 'var(--ink-70)' }}>
                    {sorted.length} {t('records') || 'records'}
                  </td>
                  <td style={{ fontWeight: 800, fontSize: 15, padding: '10px 12px', color: 'var(--ink-90)', whiteSpace: 'nowrap' }}>
                    {fmt(sorted.reduce((s, e) => s + (e.amount || 0), 0))}
                  </td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* ── Printable receipt ── */}
      <Modal open={!!receipt} onClose={() => setReceipt(null)} title={t('expenseReceipt')} size="sm"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setReceipt(null)}>{t('close')}</button>
          <button className="btn btn-primary" onClick={() => window.print()}><FileText size={14}/>{t('print')}</button>
        </>}>
        {receipt && <ExpenseReceipt exp={receipt} t={t}/>}
      </Modal>

      {/* ── Create / Edit Modal ── */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editId ? (t('editExpense') || 'Edit Expense') : (t('newExpense') || 'New Expense')}
        size="lg"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>{t('cancel') || 'Cancel'}</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? (t('saving') || 'Saving…') : (t('save') || 'Save')}
            </button>
          </div>
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>
          {/* Date */}
          <div className="form-group">
            <label className="form-label">{t('date') || 'Date'} *</label>
            <input type="date" className="form-input" value={form.date}
              onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
          </div>

          {/* Amount */}
          <div className="form-group">
            <label className="form-label">{t('amount') || 'Amount'} *</label>
            <input type="number" className="form-input" placeholder="0.00" min="0" step="0.01"
              value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
          </div>

          {/* Category */}
          <div className="form-group">
            <label className="form-label">{t('category') || 'Category'} *</label>
            <select className="form-input" value={form.category}
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
              {CATEGORIES.map(c => (
                <option key={c.key} value={c.key}>
                  {t(`cat_${c.key}`) || c.key.charAt(0).toUpperCase() + c.key.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div className="form-group">
            <label className="form-label">{t('status') || 'Status'}</label>
            <select className="form-input" value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </div>

          {/* Description - full width */}
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label className="form-label">{t('description') || 'Description'} *</label>
            <input className="form-input" placeholder={t('expenseDescriptionPlaceholder') || 'What was purchased or paid for?'}
              value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>

          {/* Vendor */}
          <div className="form-group">
            <label className="form-label">{t('vendor') || 'Vendor / Contractor'}</label>
            <input className="form-input" placeholder={t('vendorPlaceholder') || 'Company or person name'}
              value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} />
          </div>

          {/* Payment method */}
          <div className="form-group">
            <label className="form-label">{t('paymentMethod') || 'Payment Method'}</label>
            <select className="form-input" value={form.paymentMethod}
              onChange={e => setForm(f => ({ ...f, paymentMethod: e.target.value }))}>
              {METHODS.map(m => (
                <option key={m} value={m}>{t(`method_${m}`) || m.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
              ))}
            </select>
          </div>

          {/* Property */}
          <div className="form-group">
            <label className="form-label">{t('property') || 'Property'}</label>
            <select className="form-input" value={form.propertyId}
              onChange={e => setForm(f => ({ ...f, propertyId: e.target.value }))}>
              <option value="">{t('none') || 'None'}</option>
              {properties.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label className="form-label">{t('notes') || 'Notes'}</label>
            <input className="form-input" placeholder={t('optionalNotes') || 'Optional notes…'}
              value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>

          {/* Receipt upload - full width */}
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label className="form-label">{t('receiptAttachment') || 'Receipt / Invoice'}</label>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button type="button" className="btn btn-secondary btn-sm"
                onClick={() => fileRef.current?.click()}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Paperclip size={13} />
                {file ? file.name : (t('uploadReceipt') || 'Upload Receipt')}
              </button>
              {file && (
                <button type="button" className="btn btn-ghost btn-sm btn-icon"
                  onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }}>
                  <X size={13} />
                </button>
              )}
              {!file && editId && form.receiptKey && (
                <a href={fileViewUrl(form.receiptKey)} target="_blank" rel="noreferrer"
                  className="btn btn-ghost btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Paperclip size={12} /> {t('viewCurrent') || 'View current'}
                </a>
              )}
            </div>
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" style={{ display: 'none' }}
              onChange={e => setFile(e.target.files[0] || null)} />
          </div>
        </div>
      </Modal>

      {/* ── Import Preview Modal ── */}
      <Modal
        open={importOpen}
        onClose={() => { setImportOpen(false); setImportRows([]); }}
        title={t('importExpenses') || 'Import Expenses'}
        size="xl"
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={downloadTemplate} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FileText size={13} />{t('downloadTemplate') || 'Download Template'}
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => { setImportOpen(false); setImportRows([]); }}>{t('cancel') || 'Cancel'}</button>
              <button className="btn btn-primary" onClick={handleImport} disabled={importing || !importRows.some(r => r.valid)}>
                {importing ? (t('importing') || 'Importing…') : `${t('importRows') || 'Import'} ${importRows.filter(r => r.valid).length} ${t('rows') || 'rows'}`}
              </button>
            </div>
          </div>
        }
      >
        {/* Summary bar */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 14, padding: '10px 14px', borderRadius: 8, background: 'var(--surface-2)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#059669', fontWeight: 600 }}>
            <CheckCircle size={15} />{importRows.filter(r => r.valid).length} {t('validRows') || 'valid'}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#e11d48', fontWeight: 600 }}>
            <AlertCircle size={15} />{importRows.filter(r => !r.valid).length} {t('invalidRows') || 'with errors (will be skipped)'}
          </span>
        </div>

        {/* Preview table */}
        <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
          <table className="table" style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>{t('date') || 'Date'}</th>
                <th>{t('category') || 'Category'}</th>
                <th>{t('description') || 'Description'}</th>
                <th>{t('vendor') || 'Vendor'}</th>
                <th>{t('amount') || 'Amount'}</th>
                <th>{t('property') || 'Property'}</th>
                <th>{t('status') || 'Status'}</th>
                <th>{t('statusColumn') || 'Result'}</th>
              </tr>
            </thead>
            <tbody>
              {importRows.map((r, i) => (
                <tr key={i} style={{ background: r.valid ? 'rgba(5,150,105,0.04)' : 'rgba(225,29,72,0.05)' }}>
                  <td style={{ color: 'var(--ink-40)' }}>{r.row}</td>
                  <td>{r.data.date || '—'}</td>
                  <td style={{ textTransform: 'capitalize' }}>{r.data.category || '—'}</td>
                  <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.data.description || '—'}</td>
                  <td>{r.data.vendor || '—'}</td>
                  <td>{r.data.amount ? `$${parseFloat(r.data.amount).toLocaleString()}` : '—'}</td>
                  <td>{r.data.property || '—'}</td>
                  <td style={{ textTransform: 'capitalize' }}>{r.data.status}</td>
                  <td>
                    {r.valid
                      ? <span style={{ color: '#059669', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle size={13} /> OK</span>
                      : <span style={{ color: '#e11d48', fontSize: 11 }}>{r.errors.join(', ')}</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>
    </div>
  );
}

// Printable expense receipt. Sized to fit a modal and an A4 page without
// scrolling, matching the payment receipt so the two read as one family.
//
// The QR points at the public verification endpoint and carries nothing but
// the receipt number and its signature: the page it opens renders the stored
// record, so a tampered printout contradicts itself the moment it is scanned.
function ExpenseReceipt({ exp, t }) {
  const [qrDataUrl, setQrDataUrl] = useState('');

  const verifyUrl = useMemo(() => {
    if (!exp.receiptNo || !exp.verifyToken) return null;
    const base = window.location.origin;
    return `${base}/api/expenses/verify?r=${encodeURIComponent(exp.receiptNo)}&s=${encodeURIComponent(exp.verifyToken)}`;
  }, [exp.receiptNo, exp.verifyToken]);

  useEffect(() => {
    if (!verifyUrl) return;
    QRCode.toDataURL(verifyUrl, { width: 112, margin: 1, color: { dark: '#1a1a2e', light: '#f8f9fa' } })
      .then(setQrDataUrl)
      .catch(() => {});
  }, [verifyUrl]);

  const rows = [
    [t('receiptNumber'), exp.receiptNo],
    [t('date'),          format.date(exp.date)],
    [t('category'),      t(`cat_${exp.category}`)],
    [t('vendor'),        exp.vendor],
    [t('property'),      exp.propertyId?.name],
    [t('method'),        t(`method_${exp.paymentMethod || 'other'}`)],
    [t('workOrder'),     exp.workOrderId?.ticketNo],
    [t('recordedBy'),    exp.createdBy?.name],
  ].filter(([, v]) => v);

  return (
    <div style={{ fontFamily:'var(--font-mono)', background:'var(--surface-2)', borderRadius:12, padding:'14px 16px', border:'1px solid var(--border)' }}>

      <div style={{ textAlign:'center', marginBottom:10 }}>
        <div style={{ fontFamily:'var(--font-display)', fontSize:17, color:'var(--brand)', lineHeight:1.1 }}>PropManager</div>
        <div style={{ fontSize:11, fontWeight:700, color:'var(--ink-80)', letterSpacing:'.08em', marginTop:1 }}>MERCURY</div>
        <div style={{ fontSize:8.5, color:'var(--ink-40)', letterSpacing:'.1em', marginTop:3, textTransform:'uppercase' }}>
          {t('expenseReceipt')}
        </div>
      </div>

      <div style={{ borderTop:'2px dashed var(--border)', borderBottom:'2px dashed var(--border)', padding:'9px 0', marginBottom:9 }}>
        {rows.map(([k, v]) => (
          <div key={k} style={{ display:'flex', justifyContent:'space-between', gap:10, fontSize:11.5, lineHeight:1.5, marginBottom:1 }}>
            <span style={{ color:'var(--ink-40)' }}>{k}</span>
            <span style={{ fontWeight:600, textAlign:'right' }}>{v}</span>
          </div>
        ))}
      </div>

      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:8.5, color:'var(--ink-40)', letterSpacing:'.08em', marginBottom:1, textTransform:'uppercase' }}>{t('amountPaid')}</div>
        <div style={{ fontSize:26, fontWeight:900, color:'var(--rose)', fontFamily:'var(--font-display)', lineHeight:1.15 }}>
          {format.currencyFull(exp.amount)}
        </div>
      </div>

      {exp.description && (
        <div style={{ marginTop:7, fontSize:11, color:'var(--ink-60)', textAlign:'center', lineHeight:1.4 }}>{exp.description}</div>
      )}
      {exp.notes && (
        <div style={{ marginTop:4, fontSize:10, color:'var(--ink-40)', textAlign:'center' }}>{exp.notes}</div>
      )}

      {qrDataUrl && (
        <div style={{ marginTop:9, paddingTop:9, borderTop:'1px dashed var(--border)', display:'flex', alignItems:'center', gap:10 }}>
          <img src={qrDataUrl} alt={t('scanToVerify')} style={{ width:56, height:56, borderRadius:5, border:'1px solid var(--border)', flexShrink:0 }}/>
          <div style={{ fontSize:9, color:'var(--ink-40)', lineHeight:1.45, minWidth:0 }}>
            <div style={{ fontWeight:700, fontSize:9.5, color:'var(--ink-60)', marginBottom:2, textTransform:'uppercase', letterSpacing:'.05em' }}>
              {t('digitalVerification')}
            </div>
            <div>{t('scanToVerify')}</div>
            <div style={{ marginTop:2, fontFamily:'var(--font-mono)', fontSize:8, color:'var(--ink-20)', wordBreak:'break-all' }}>
              SIG: {exp.verifyToken}
            </div>
          </div>
        </div>
      )}
      {!verifyUrl && (
        <div style={{ marginTop:8, fontSize:9, color:'var(--ink-20)', textAlign:'center' }}>{t('noDigitalSignature')}</div>
      )}

      <div style={{ textAlign:'center', marginTop:8, fontSize:9, color:'var(--ink-20)' }}>{t('expenseReceiptFooter')}</div>
    </div>
  );
}
